export const mockData = {
  company: {
    name: 'YardEdge',
    tagline: 'Power the Future from Your Backyard',
    description: 'Transform your home into a sustainable data infrastructure hub with solar-powered mini data centers'
  },
  
  hero: {
    title: 'Your Backyard, The Next Data Frontier',
    subtitle: 'Install mini data centers powered by renewable energy and earn passive income while supporting the digital revolution',
    cta: 'Get Started',
    secondaryCta: 'Learn More'
  },
  
  benefits: [
    {
      id: 1,
      icon: 'Zap',
      title: 'Generate Passive Income',
      description: 'Earn monthly revenue by hosting edge computing infrastructure in your unused outdoor space'
    },
    {
      id: 2,
      icon: 'Leaf',
      title: '100% Renewable Energy',
      description: 'Powered entirely by solar panels, reducing carbon footprint while maximizing efficiency'
    },
    {
      id: 3,
      icon: 'Shield',
      title: 'Zero Maintenance',
      description: 'Fully managed systems with 24/7 remote monitoring and automated maintenance protocols'
    },
    {
      id: 4,
      icon: 'TrendingUp',
      title: 'Property Value Boost',
      description: 'Increase home value with cutting-edge sustainable technology infrastructure'
    },
    {
      id: 5,
      icon: 'Cpu',
      title: 'Edge Computing Ready',
      description: 'Support next-gen AI, IoT, and low-latency applications from your neighborhood'
    },
    {
      id: 6,
      icon: 'DollarSign',
      title: 'Tax Incentives',
      description: 'Qualify for renewable energy tax credits and infrastructure investment benefits'
    }
  ],
  
  howItWorks: [
    {
      id: 1,
      step: '01',
      title: 'Site Assessment',
      description: 'Our team evaluates your property for optimal placement, sun exposure, and connectivity requirements'
    },
    {
      id: 2,
      step: '02',
      title: 'Custom Design',
      description: 'We design a solar-powered mini data center tailored to your space and energy capacity'
    },
    {
      id: 3,
      step: '03',
      title: 'Professional Installation',
      description: 'Certified technicians install the unit with minimal disruption, typically completed in 2-3 days'
    },
    {
      id: 4,
      step: '04',
      title: 'Start Earning',
      description: 'Once online, your data center begins processing workloads and generating passive income immediately'
    }
  ],
  
  techSpecs: {
    physical: [
      { label: 'Dimensions', value: '8ft x 6ft x 7ft' },
      { label: 'Weight', value: '1,200 lbs' },
      { label: 'Placement', value: 'Ground or concrete pad' },
      { label: 'Noise Level', value: '<45 dB (whisper quiet)' }
    ],
    power: [
      { label: 'Solar Capacity', value: '15-20 kW' },
      { label: 'Battery Storage', value: '40 kWh' },
      { label: 'Grid Backup', value: 'Automatic failover' },
      { label: 'Energy Efficiency', value: 'PUE 1.2' }
    ],
    computing: [
      { label: 'Server Capacity', value: '4-8 rack units' },
      { label: 'Processing Power', value: 'Up to 384 cores' },
      { label: 'Memory', value: 'Up to 3TB RAM' },
      { label: 'Storage', value: 'Up to 500TB NVMe' }
    ],
    connectivity: [
      { label: 'Network Speed', value: '10 Gbps fiber' },
      { label: 'Latency', value: '<5ms to edge' },
      { label: 'Uptime SLA', value: '99.9%' },
      { label: 'Redundancy', value: 'Multi-path' }
    ]
  },
  
  pricingPlans: [
    {
      id: 1,
      name: 'Starter',
      price: '$15,000',
      revenue: '$300-500/mo',
      features: [
        'Basic 4-rack unit configuration',
        '15 kW solar system',
        'Standard monitoring',
        'Email support',
        '2-year warranty'
      ],
      popular: false
    },
    {
      id: 2,
      name: 'Professional',
      price: '$28,000',
      revenue: '$600-900/mo',
      features: [
        'Enhanced 6-rack unit setup',
        '18 kW solar + battery storage',
        'Advanced AI monitoring',
        'Priority 24/7 support',
        '5-year warranty',
        'Free maintenance for 2 years'
      ],
      popular: true
    },
    {
      id: 3,
      name: 'Enterprise',
      price: '$45,000',
      revenue: '$1,200-1,800/mo',
      features: [
        'Maximum 8-rack configuration',
        '20 kW solar + extended battery',
        'Real-time analytics dashboard',
        'Dedicated account manager',
        '10-year warranty',
        'Lifetime maintenance included'
      ],
      popular: false
    }
  ],
  
  faqs: [
    {
      id: 1,
      question: 'How much space do I need?',
      answer: 'A minimum of 10ft x 8ft of outdoor space is required, with good sun exposure for optimal solar panel performance. Most backyards and side yards work perfectly.'
    },
    {
      id: 2,
      question: 'Will it be noisy?',
      answer: 'Our units operate at under 45 decibels - quieter than a normal conversation. Advanced cooling systems and sound insulation ensure minimal noise pollution.'
    },
    {
      id: 3,
      question: 'What about permits and regulations?',
      answer: 'We handle all permitting, zoning approvals, and compliance requirements. Our team works with local authorities to ensure full regulatory compliance.'
    },
    {
      id: 4,
      question: 'How do I earn money?',
      answer: 'Your data center hosts edge computing workloads for businesses. You receive monthly payments based on uptime and processing capacity utilized.'
    },
    {
      id: 5,
      question: 'What happens if something breaks?',
      answer: 'All plans include comprehensive warranties and remote monitoring. Our team detects issues before they become problems and dispatches technicians if needed.'
    },
    {
      id: 6,
      question: 'Can I still use my own electricity?',
      answer: 'Yes! Excess solar energy can power your home first, with surplus going to the data center. Some homeowners see reduced electricity bills.'
    }
  ],
  
  gallery: [
    {
      id: 1,
      url: 'https://images.unsplash.com/photo-1694889649834-91ff242d1763?w=800&q=80',
      alt: 'Tesla at modern home garage',
      category: 'lifestyle'
    },
    {
      id: 2,
      url: 'https://images.unsplash.com/photo-1761472823286-9f6093ed6663?w=800&q=80',
      alt: 'Modern neighborhood with solar panels',
      category: 'solar'
    },
    {
      id: 3,
      url: 'https://images.unsplash.com/photo-1650682009477-52fd77302b78?w=800&q=80',
      alt: 'Smart home technology hub',
      category: 'technology'
    },
    {
      id: 4,
      url: 'https://images.unsplash.com/photo-1753039495488-434a2fe53e41?w=800&q=80',
      alt: 'Modern smart home sensor',
      category: 'technology'
    },
    {
      id: 5,
      url: 'https://images.unsplash.com/photo-1668097613572-40b7c11c8727?w=800&q=80',
      alt: 'Solar panel installation work',
      category: 'solar'
    },
    {
      id: 6,
      url: 'https://images.pexels.com/photos/4320480/pexels-photo-4320480.jpeg?w=800&q=80',
      alt: 'Solar array installation',
      category: 'solar'
    },
    {
      id: 7,
      url: 'https://images.unsplash.com/photo-1548544027-1a96c4c24c7a?w=800&q=80',
      alt: 'Server rack infrastructure',
      category: 'technology'
    },
    {
      id: 8,
      url: 'https://images.unsplash.com/photo-1667264501379-c1537934c7ab?w=800&q=80',
      alt: 'Professional data center equipment',
      category: 'technology'
    }
  ],
  
  video: {
    embedUrl: 'https://www.youtube.com/embed/6NbRmphB0oU',
    thumbnail: 'https://images.unsplash.com/photo-1696324535687-6bb987f50e96?w=1200&q=80',
    title: 'Modular Data Center Installation - Timelapse'
  }
};